import { useEffect, useState } from 'react'
import apiClient from '../api/client'
import styles from './FacebookCampaignsPage.module.css'

interface FbCampaign {
  id: string
  name?: string
  status?: string
  objective?: string
  created_time?: string
}

function StatusBadge({ status }: { status?: string }) {
  const s = (status || '').toUpperCase()
  let cls = styles.badgeDefault
  if (s === 'ACTIVE') cls = styles.badgeActive
  else if (s === 'PAUSED') cls = styles.badgePaused
  else if (s === 'ARCHIVED' || s === 'DELETED') cls = styles.badgeArchived

  return (
    <span className={`${styles.badge} ${cls}`}>
      <span className={styles.dot} />
      {status || 'Unknown'}
    </span>
  )
}

export function FacebookCampaignsPage() {
  const [campaigns, setCampaigns] = useState<FbCampaign[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchCampaigns = async () => {
    setLoading(true); setError(null)
    try {
      const res = await apiClient.get('/facebook-campaigns')
      const data = res.data
      const safe: FbCampaign[] =
        Array.isArray(data) ? data
        : Array.isArray(data?.data) ? data.data
        : Array.isArray(data?.campaigns) ? data.campaigns
        : []
      setCampaigns(safe)
    } catch (err) {
      console.error(err)
      setError('Failed to load Facebook campaigns')
      setCampaigns([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchCampaigns() }, [])

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.pageHeader}>
          <div>
            <h1 className={styles.heading}>Meta Campaigns</h1>
            <p className={styles.subheading}>Your live Facebook ad campaigns</p>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
          <div style={{
            width: 36, height: 36,
            border: '3px solid #e5e9f0',
            borderTopColor: '#1d4ed8',
            borderRadius: '50%',
            animation: 'spin 0.7s linear infinite',
          }} />
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.heading}>Meta Campaigns</h1>
          <p className={styles.subheading}>
            {campaigns.length > 0
              ? `${campaigns.length} campaign${campaigns.length !== 1 ? 's' : ''} found`
              : 'Your live Facebook ad campaigns'}
          </p>
        </div>
        <button className={styles.refreshBtn} onClick={fetchCampaigns} disabled={loading}>
          ↻ Refresh
        </button>
      </div>

      {error && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fca5a5',
          borderRadius: '10px', padding: '1rem 1.25rem',
          color: '#dc2626', fontSize: '0.875rem',
          display: 'flex', gap: '0.75rem', alignItems: 'center'
        }}>
          <span>⚠️</span>
          <span>{error}</span>
          <button
            onClick={fetchCampaigns}
            style={{ marginLeft: 'auto', padding: '0.3rem 0.75rem',
              border: '1px solid #fca5a5', borderRadius: '6px',
              background: 'transparent', color: '#dc2626',
              cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}
          >
            Retry
          </button>
        </div>
      )}

      {!error && campaigns.length === 0 ? (
        <div className={styles.empty}>
          <span className={styles.emptyIcon}>📢</span>
          <h3 className={styles.emptyTitle}>No Meta campaigns found</h3>
          <p className={styles.emptyText}>
            Connect your Facebook account in Settings, then publish a campaign from the Create page to see it here.
          </p>
        </div>
      ) : (
        <div className={styles.grid}>
          {campaigns.map(c => (
            <div key={c.id} className={styles.card}>
              <div className={styles.cardTop}>
                <span className={styles.cardName}>{c.name || 'Unnamed Campaign'}</span>
                <StatusBadge status={c.status} />
              </div>

              <div className={styles.metaRow}>
                {c.objective && (
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Objective</span>
                    <span className={styles.metaValue}>{c.objective.replace(/_/g, ' ')}</span>
                  </div>
                )}
                {c.created_time && (
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Created</span>
                    <span className={styles.metaValue}>
                      {new Date(c.created_time).toLocaleDateString('en-IN', {
                        day: 'numeric', month: 'short', year: 'numeric'
                      })}
                    </span>
                  </div>
                )}
              </div>

              <span className={styles.cardId}>ID: {c.id}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
